/**
 * ServiceDetailsStep — Step 1 of the service checkout variant.
 *
 * Renders:
 *   - Product preview (title + seller name).
 *   - Date picker (single day — Q4) with the cancellation-policy hint.
 *   - Time range (start + end — Q4) + the inline "La hora de fin debe ser
 *     posterior a la hora de inicio" error when end ≤ start.
 *   - Guest count input (free integer ≥1 — Q5).
 *   - Deposit / balance breakdown card.
 *   - Inline error alert + footer (Cancelar / Continuar → Step 2).
 *
 * SPEC-244 · partición VERBATIM de
 * frontend/src/components/market/checkout-modal.tsx (2.637 L).
 *
 * Preserved VERBATIM — the original Step 1 JSX block (lines 1954-2141 of
 * the monolith).
 */

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DialogFooter } from '@/components/ui/dialog';
import { ArrowRight, Calendar, Users, Clock } from 'lucide-react';
import type * as React from 'react';
import { formatCredits } from '../helpers';
import { SubmitErrorAlert } from './submit-error-alert';

// ─── Component ───────────────────────────────────────────────────────────────

export interface ServiceDetailsStepProps {
  product: import('@profileia/shared').PublicProductResponse;
  reservationDate: string;
  setReservationDate: React.Dispatch<React.SetStateAction<string>>;
  reservationTimeStart: string;
  setReservationTimeStart: React.Dispatch<React.SetStateAction<string>>;
  reservationTimeEnd: string;
  setReservationTimeEnd: React.Dispatch<React.SetStateAction<string>>;
  guestCount: number;
  setGuestCount: React.Dispatch<React.SetStateAction<number>>;
  depositPercent: number;
  grandTotal: number;
  depositAmount: number;
  balanceAmount: number;
  detailsValid: boolean;
  submitError: string | null;
  onClose: () => void;
  onContinue: () => void;
}

export function ServiceDetailsStep({
  product,
  reservationDate,
  setReservationDate,
  reservationTimeStart,
  setReservationTimeStart,
  reservationTimeEnd,
  setReservationTimeEnd,
  guestCount,
  setGuestCount,
  depositPercent,
  grandTotal,
  depositAmount,
  balanceAmount,
  detailsValid,
  submitError,
  onClose,
  onContinue,
}: ServiceDetailsStepProps) {
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/40 p-3">
        <p className="text-sm font-medium line-clamp-2">{product.title}</p>
        <p className="text-xs text-muted-foreground mt-1">
          Vendido por <span className="font-medium">{product.seller.storeName}</span>
        </p>
      </div>

      {/* Date picker (single day — Q4) */}
      <div>
        <Label
          htmlFor="reservation-date"
          className="mb-1.5 block text-sm font-medium"
        >
          <Calendar className="size-4 inline mr-1 -mt-0.5" aria-hidden="true" />
          Fecha de la reserva
        </Label>
        <Input
          id="reservation-date"
          type="date"
          value={reservationDate}
          onChange={(e) => setReservationDate(e.target.value)}
          min={new Date().toISOString().split('T')[0]}
          aria-label="Selecciona la fecha de la reserva"
          required
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Selecciona un único día. La política de cancelación es{' '}
          <span className="font-medium">
            {product.serviceConfig?.cancellationPolicy === 'no_refund'
              ? 'sin reembolso'
              : 'cancelación gratuita hasta 48h antes'}
          </span>
          .
        </p>
      </div>

      {/* Time range (start + end — Q4) */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label
            htmlFor="reservation-time-start"
            className="mb-1.5 block text-sm font-medium"
          >
            <Clock className="size-4 inline mr-1 -mt-0.5" aria-hidden="true" />
            Hora de inicio
          </Label>
          <Input
            id="reservation-time-start"
            type="time"
            value={reservationTimeStart}
            onChange={(e) => setReservationTimeStart(e.target.value)}
            aria-label="Selecciona la hora de inicio"
            required
          />
        </div>
        <div>
          <Label
            htmlFor="reservation-time-end"
            className="mb-1.5 block text-sm font-medium"
          >
            <Clock className="size-4 inline mr-1 -mt-0.5" aria-hidden="true" />
            Hora de fin
          </Label>
          <Input
            id="reservation-time-end"
            type="time"
            value={reservationTimeEnd}
            onChange={(e) => setReservationTimeEnd(e.target.value)}
            aria-label="Selecciona la hora de fin"
            required
          />
        </div>
      </div>
      {reservationTimeStart && reservationTimeEnd &&
        reservationTimeEnd <= reservationTimeStart && (
          <p className="text-xs text-destructive">
            La hora de fin debe ser posterior a la hora de inicio.
          </p>
        )}

      {/* Guest count (free integer ≥1 — Q5) */}
      <div>
        <Label
          htmlFor="reservation-guests"
          className="mb-1.5 block text-sm font-medium"
        >
          <Users className="size-4 inline mr-1 -mt-0.5" aria-hidden="true" />
          Número de invitados
        </Label>
        <Input
          id="reservation-guests"
          type="number"
          min={1}
          step={1}
          value={guestCount}
          onChange={(e) => {
            const raw = parseInt(e.target.value, 10);
            if (Number.isNaN(raw)) {
              setGuestCount(1);
              return;
            }
            setGuestCount(Math.max(1, Math.floor(raw)));
          }}
          className="w-32"
          aria-label="Número de invitados (mínimo 1)"
          required
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Mínimo 1. El vendedor coordina contigo para verificar disponibilidad de capacidad.
        </p>
      </div>

      {/* Deposit / balance breakdown */}
      <div className="rounded-md border border-border bg-muted/40 p-3 space-y-2">
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">Precio del servicio</span>
          <span className="font-medium tabular-nums">
            {formatCredits(grandTotal)} créditos
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            Depósito ({depositPercent}%)
          </span>
          <span className="font-bold tabular-nums text-primary">
            {formatCredits(depositAmount)} créditos
          </span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-muted-foreground">
            Balance ({100 - depositPercent}%)
          </span>
          <span className="font-medium tabular-nums">
            {formatCredits(balanceAmount)} créditos
          </span>
        </div>
        <p className="text-xs text-muted-foreground border-t border-border pt-2">
          Pagas el depósito ahora. El balance se carga al completar el servicio.
        </p>
      </div>

      {submitError && <SubmitErrorAlert submitError={submitError} />}

      <DialogFooter className="sticky bottom-0 -mx-6 -mb-6 mt-2 flex-row gap-2 border-t border-border bg-background px-6 py-3 sm:flex-row">
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button
          type="button"
          className="flex-1"
          disabled={!detailsValid}
          onClick={onContinue}
        >
          Continuar
          <ArrowRight className="size-4 ml-1" aria-hidden="true" />
        </Button>
      </DialogFooter>
    </div>
  );
}
